import React, { useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';
import { useForm } from 'react-hook-form';
import { useQueryClient } from '@tanstack/react-query';
import { LoadingButton } from '@mui/lab';
import { yupResolver } from '@hookform/resolvers/yup';
import { Grid, Box, Button } from '@mui/material';
import { GeneralSelectMenu } from 'src/components/general';
import { FormProvider, RHFTextField } from 'src/components/hook-form';
import { schema } from './validationSchema';
import { getDefaultValues } from './defaultValues';
import { generateSelectMenuItems } from 'src/utils';
import { networkCreateGatewayProfile, networkUpdateGatewayProfile } from 'src/network/gateway-profile';
import { useFetchProjectList } from 'src/network/application';
import { GATEWAY_PROFILE_LIST } from 'src/constants/queryKeys';
import useFormsModalStore from 'src/store/forms';

const GatewayProfileForm = () => {
  const { modal, closeModal } = useFormsModalStore();

  const queryClient = useQueryClient();

  const { enqueueSnackbar } = useSnackbar();

  const [projectList, setProjectList] = useState([]);
  const [selectedProject, setSelectedProject] = useState('');

  const gatewayProfileDetails = modal?.data;
  const isEditMode = !!gatewayProfileDetails;

  const defaultValues = getDefaultValues(gatewayProfileDetails);

  const methods = useForm({
    // resolver: yupResolver(schema), // todo schema
    defaultValues,
  });

  const {
    reset,
    handleSubmit,
    formState: { isSubmitting },
  } = methods;

  const onSubmit = async (values, event) => {
    const PAYLOAD = {
      ...values,
      GatewayProfileApplicationID: selectedProject,
    };

    try {
      if (isEditMode) {
        const res = await networkUpdateGatewayProfile(gatewayProfileDetails?.GatewayProfileID, PAYLOAD);
        if (res.data?.items?.ResultID !== -1) {
          enqueueSnackbar('آیتم موردنظر با موفقیت ویرایش شد');
          if (event.nativeEvent.submitter.name === 'save') {
            closeModal();
            reset();
          }
          await queryClient.invalidateQueries({ queryKey: [GATEWAY_PROFILE_LIST] });
        } else {
          enqueueSnackbar('خطایی در ویرایش آیتم رخ داد!', { variant: 'error' });
        }
      } else {
        const res = await networkCreateGatewayProfile(PAYLOAD);
        if (res.data?.items?.ResultID !== -1) {
          enqueueSnackbar('آیتم موردنظر با موفقیت ایجاد شد');
          if (event.nativeEvent.submitter.name === 'save') {
            closeModal();
          }
          reset();
          await queryClient.invalidateQueries({ queryKey: [GATEWAY_PROFILE_LIST] });
        } else {
          enqueueSnackbar('خطایی در ایجاد آیتم رخ داد!', { variant: 'error' });
        }
      }
    } catch (error) {
      console.error(error);
      enqueueSnackbar('خطای ناشناخته رخ داد!', { variant: 'error' });
    }
  };

  const { data: projects } = useFetchProjectList();

  useEffect(() => {
    if (projects) {
      const result = projects?.data.items?.map((item) => {
        return { title: item.ApplicationTitle, appId: item.ApplicationID };
      });

      const projectMenuItems = generateSelectMenuItems(result, 'title', 'appId');

      setProjectList([...projectMenuItems]);
      setSelectedProject(gatewayProfileDetails?.GatewayProfileApplicationID || projectMenuItems?.[0]?.id);
    }
  }, [projects]);

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Box sx={{ p: 3 }}>
            <Box
              sx={{
                display: 'grid',
                columnGap: 2,
                rowGap: 3,
                gridTemplateColumns: { xs: 'repeat(1, 1fr)', sm: 'repeat(2, 1fr)' },
              }}
            >
              <RHFTextField name="GatewayProfileLabel" type="text" label="عنوان درگاه" />

              <RHFTextField name="GatewayProfilePackageName" type="text" label="عنوان بسته فروشگاه" />

              <RHFTextField name="GatewayProfileCallback" type="text" label="لینک بازگشت از خرید" />

              <GeneralSelectMenu
                label="نام محصول/پروژه"
                name="GatewayProfileApplicationID"
                options={projectList}
                value={selectedProject}
                onChangeValue={setSelectedProject}
              />
            </Box>

            <div className="flex items-center gap-4 mt-6">
              <LoadingButton type="submit" variant="contained" name="save" loading={isSubmitting}>
                ذخیره
              </LoadingButton>
              <LoadingButton type="submit" variant="contained" color="info" name="saveAndContinue" loading={isSubmitting}>
                {isEditMode ? 'ذخیره و ادامه ویرایش' : 'ذخیره و ایجاد بعدی'}
              </LoadingButton>
              <Button onClick={closeModal} variant="contained" color="error">
                انصراف
              </Button>
            </div>
          </Box>
        </Grid>
      </Grid>
    </FormProvider>
  );
};

export default GatewayProfileForm;
